import { escapeHtml } from "./String";

/**
 * Parses a JSON object and escapes the HTML that could be maliciously embedded inside it.
 *
 * The idea being that any string inside it would be completely safe to embed in HTML pages without worrying about scripts.
 *
 * @param json - the json string to parse.
 * @return An escaped string.
 */
export function parseEscape(json: string): object {
    if (json) {
        const obj: object = JSON.parse(json);
        escapeObj(obj);
        return obj;
    }
    return undefined;
}

/**
 * A function that will stringify all the elements in a JSON before stringifying the entire JSON.
 * @param obj The JSON object to stringify.
 * @return a Stringified version of the JSON with all the parameters escaped.
 */
export function stringifyEscape(obj: object): string {
    if (obj) {
        const copy = { ...obj };
        escapeObj(copy);
        return JSON.stringify(copy);
    }
    return undefined;
}

/**
 * A function that will escape all the string values inside the JSON object.
 * @param obj The object in which to escape.  This object will be modified.
 */
export function escapeObj(obj: any) {
    if (obj) {
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                const item = obj[key];
                const type = typeof item;
                if (type === "string") {
                    obj[key] = escapeHtml(item);
                } else if (type === "object") {
                    escapeObj(item);
                }
            }
        }
    }
}

/**
 * Removes all empty strings, nulls, and undefined(s) from the provided object.
 *
 * It does not modify the original.
 *
 * @param obj
 */
export function cleanObj<T>(obj: T): T {
    const cleanMe = { ...obj };

    for (const key in cleanMe) {
        if (cleanMe.hasOwnProperty(key)) {
            const item = cleanMe[key];
            const type = typeof item;
            if (type === "string") {
                // if it is an empty string,
                // delete it
                if (item === "") {
                    delete cleanMe[key];
                }
            } else if (!item) {
                // of it if is undefined,
                // delete it
                delete cleanMe[key];
            }
        }
    }

    return cleanMe;
}

/**
 * @deprecated Use removeKeyStrict insted
 * Removes all instances of the key from the object.
 *
 * It does not modify the original
 *
 * @param obj
 * @param key
 */
export function removeKey<T extends object>(obj: T, key: string): T {
    return JSON.parse(JSON.stringify(obj, (k, v) => (k === key ? undefined : v)));
}

export function removeKeyStrict<T extends object, TKey extends keyof T>(obj: T, key: TKey): Omit<T, TKey> {
    return JSON.parse(JSON.stringify(obj, (k, v) => (k === key ? undefined : v)));
}
